import _ from 'lodash';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  elementToCalculateStripAfibThumbnailInfo,
  elementToCalculateStripArtifactThumbnailInfo,
  elementToCalculateStripEventThumbnailInfo,
  setStripAfibThumbnailInfo,
  setStripArtifactThumbnailInfo,
  setStripEventThumbnailInfo,
} from '@/shared/config';
import { ID_DIV_STRIP_RHYTHM_EVENT } from '@/entities/event/@x/ecg';
import { SIZE_EVENT_THUMBNAIL, useReportStore } from '@/entities/report/@x/ecg';
import type { EventThumbnailConfigProps } from '../ui/eventThumbnailConfig';

// Duplicated from widgets/beats/helper.ts (and widgets/hr-summary/helper.ts, which
// duplicates it too) rather than imported: an entity importing that widget helper
// module creates entities/beat -> entities/ecg -> this file -> widgets/beats/helper
// -> entities/beat, a real circular import that breaks entities/beat's barrel exports.
const ID_DIV_STRIP_ARTIFACT = 'stripArtifactsBeatHr';
const ID_DIV_STRIP_AFIB = 'stripAfibsBeatHr';

const useEventThumbnailConfig = ({
  isArtifact = false,
  isAfib = false,
}: Pick<EventThumbnailConfigProps, 'isArtifact' | 'isAfib'>) => {
  const sizeThumbnailConfig = useReportStore((s) => s.sizeEventThumbnailConfig);
  const setSizeThumbnailConfig = useReportStore((s) => s.actions.setSizeEventThumbnailConfig);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const setChannelsThumbnail = useReportStore((s) => s.actions.setChannelsThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const setGainThumbnail = useReportStore((s) => s.actions.setGainThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const setHighPassThumbnail = useReportStore((s) => s.actions.setHighPassThumbnail);
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const setReloadViewSizeConfig = useReportStore((s) => s.actions.setReloadViewSizeConfig);

  const [size, setSize] = useState(sizeThumbnailConfig.name);
  const [gain, setGain] = useState(gainThumbnail);
  const [channels, setChannels] = useState(channelsThumbnail);
  const [highPass, setHighPass] = useState(highPassThumbnail);

  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const wrapperHeight = useRef<number>();

  /**
   * Applies the thumbnail size picked from the strip config menu. Stores the selected item's props.value as the current size.
   *
   * @param item - Selected menu item whose props.value carries the thumbnail size.
   * @returns Nothing.
   */
  const onClickItemSize = ({ item }: { item: any }) => {
    setSize(item.props.value);
  };

  /**
   * Applies the gain picked from the strip thumbnail config menu. Stores the selected item's props.value as the current gain.
   *
   * @param item - Selected menu item whose props.value carries the gain.
   * @returns Nothing.
   */
  const onClickItemGain = ({ item }: { item: any }) => {
    setGain(item.props.value);
  };

  /**
   * Applies the high-pass filter picked from the strip thumbnail config menu. Stores the selected item's props.value as the current high pass.
   *
   * @param item - Selected menu item whose props.value carries the high-pass cutoff.
   * @returns Nothing.
   */
  const onClickItemHighPass = ({ item }: { item: any }) => {
    setHighPass(item.props.value);
  };

  /**
   * Toggles which ECG channels the strip thumbnails render. Picking channel four collapses the selection to the AI channel alone, otherwise the AI channel is dropped and the picked channel added or removed before re-sorting, with an emptied selection falling back to the AI channel.
   *
   * @param name - Identifier of the toggled control; not read by the update.
   * @param value - Channel number toggled, where four is the AI channel.
   * @returns Nothing.
   */
  const onClickToggleButton = (name: any, value: any) => {
    //* Default is AI channel
    const defaultChannels = [4];
    if (value === 4) {
      setChannels((prev: any) => {
        if (_.isEqual(defaultChannels, prev)) {
          return prev;
        }
        return defaultChannels;
      });
      return;
    }
    const newChannels = [...channels];
    const indexAIChannel = newChannels.indexOf(4);
    if (indexAIChannel > -1) {
      newChannels.splice(indexAIChannel, 1);
    }
    const indexAddChannel = newChannels.indexOf(value);
    if (indexAddChannel > -1) {
      newChannels.splice(indexAddChannel, 1);
    } else {
      newChannels.push(value);
    }
    newChannels.sort();
    setChannels((prev: any) => {
      if (_.isEqual(newChannels, prev)) {
        return prev;
      }
      if (newChannels.length === 0) {
        return defaultChannels;
      }
      return newChannels;
    });
  };

  /**
   * Works out how many strip thumbnails fit across and down the current tab's viewport. Picks the artifact, AFib or rhythm-event wrapper element, caches its offsetHeight while it is visible, divides the available width and height by the thumbnail size plus the inter-strip gap using that tab's padding constants, stores the result, and nudges the view-size config to force a redraw.
   *
   * @param width - Rendered width of a single strip thumbnail.
   * @param height - Rendered height of a single strip thumbnail.
   * @returns Nothing.
   */
  const calculateStripThumbnailInfo = ({ width, height }: { width: number; height: number }) => {
    if (isArtifact) {
      const stripWrapperEl = document.getElementById(ID_DIV_STRIP_ARTIFACT);
      if (stripWrapperEl) {
        // When element is hidden, offsetHeight === 0
        if (stripWrapperEl.offsetHeight) {
          wrapperHeight.current = stripWrapperEl.offsetHeight;
        }
        const { leftColumnWidth, paddingBothSide, stripBeatGap, paddingTopBottom } =
          elementToCalculateStripArtifactThumbnailInfo;
        const stripPerRow = Math.floor(
          (window.innerWidth - leftColumnWidth - paddingBothSide + stripBeatGap) /
            (width + stripBeatGap),
        );
        const stripRow = Math.floor(
          (wrapperHeight.current - paddingTopBottom + stripBeatGap) / (height + stripBeatGap),
        );
        setStripArtifactThumbnailInfo({ stripPerRow, stripRow });
      }
    } else if (isAfib) {
      const stripWrapperEl = document.getElementById(ID_DIV_STRIP_AFIB);
      if (stripWrapperEl) {
        // When element is hidden, offsetHeight === 0
        if (stripWrapperEl.offsetHeight) {
          wrapperHeight.current = stripWrapperEl.offsetHeight;
        }
        const { leftColumnWidth, paddingBothSide, stripBeatGap, paddingTopBottom } =
          elementToCalculateStripAfibThumbnailInfo;
        const stripPerRow = Math.floor(
          (window.innerWidth - leftColumnWidth - paddingBothSide + stripBeatGap) /
            (width + stripBeatGap),
        );
        const stripRow = Math.floor(
          (wrapperHeight.current - paddingTopBottom + stripBeatGap) / (height + stripBeatGap),
        );
        setStripAfibThumbnailInfo({ stripPerRow, stripRow });
      }
    } else {
      const stripWrapperEl = document.getElementById(ID_DIV_STRIP_RHYTHM_EVENT);
      if (stripWrapperEl) {
        // When element is hidden, offsetHeight === 0
        if (stripWrapperEl.offsetHeight) {
          wrapperHeight.current = stripWrapperEl.offsetHeight;
        }
        const { leftColumnWidth, paddingBothSide, stripBeatGap, paddingTopBottom } =
          elementToCalculateStripEventThumbnailInfo;
        const stripPerRow = Math.floor(
          (window.innerWidth - leftColumnWidth - paddingBothSide + stripBeatGap) /
            (width + stripBeatGap),
        );
        const stripRow = Math.floor(
          (wrapperHeight.current - paddingTopBottom + stripBeatGap) / (height + stripBeatGap),
        );
        setStripEventThumbnailInfo({ stripPerRow, stripRow });
      }
    }
    setReloadViewSizeConfig((prev) => prev + Math.random() * 100);
  };

  useEffect(() => {
    timeout.current = setTimeout(() => {
      if (!_.isEqual(channels, channelsThumbnail)) {
        setChannelsThumbnail(channels);
      }
      if (gain !== gainThumbnail) {
        setGainThumbnail(gain);
      }
      if (highPass !== highPassThumbnail) {
        setHighPassThumbnail(highPass);
      }
    }, 500);
    return () => {
      clearTimeout(timeout.current);
    };
  }, [channels, gain, highPass]);

  useEffect(() => {
    if (!_.isEqual(channels, channelsThumbnail)) {
      setChannels(channelsThumbnail);
    }
    if (gain !== gainThumbnail) {
      setGain(gainThumbnail);
    }
    if (highPass !== highPassThumbnail) {
      setHighPass(highPassThumbnail);
    }
  }, [channelsThumbnail, gainThumbnail, highPassThumbnail]);

  useEffect(() => {
    if (size !== sizeThumbnailConfig.name) {
      //* Change from current tab
      const newSizeThumbnail = SIZE_EVENT_THUMBNAIL[size];
      calculateStripThumbnailInfo({
        width: newSizeThumbnail.width,
        height: newSizeThumbnail.height,
      });
      setSizeThumbnailConfig(newSizeThumbnail);
    }
  }, [size]);

  useEffect(() => {
    if (size !== sizeThumbnailConfig.name) {
      //* Change from other tab
      calculateStripThumbnailInfo({
        width: sizeThumbnailConfig.width,
        height: sizeThumbnailConfig.height,
      });
      setSize(sizeThumbnailConfig.name);
    }
  }, [sizeThumbnailConfig]);

  useLayoutEffect(() => {
    const { width, height } = sizeThumbnailConfig;
    calculateStripThumbnailInfo({ width, height });
  }, []);

  return {
    ecgDataMap,
    size,
    gain,
    channels,
    highPass,
    onClickItemSize,
    onClickItemGain,
    onClickItemHighPass,
    onClickToggleButton,
  };
};

export default useEventThumbnailConfig;
